// Job Polling Utility
// Provides enqueueAndPoll function for submitting jobs and polling their status
import aiQueue from './aiQueue';
import { backendConfig } from '../config/backendConfig';

/**
 * Enqueue a job and poll for its completion
 * @param {string} endpoint - API endpoint URL or endpoint name
 * @param {object} data - Request data to send
 * @param {object} options - Polling options
 * @param {number} options.interval - Polling interval in milliseconds (default: 2000)
 * @param {number} options.maxAttempts - Maximum polling attempts (default: 150)
 * @param {function} options.onProgress - Progress callback (status, job, attempts)
 * @param {function} options.onStatusChange - Status change callback (status)
 * @param {string} options.baseUrl - Base URL for API calls
 * @returns {Promise} Promise that resolves with the job result
 */
export async function enqueueAndPoll(endpoint, data, options = {}) {
  const {
    interval = 2000,
    maxAttempts = 150,
    onProgress,
    onStatusChange,
    baseUrl
  } = options;

  // Determine the base URL
  const apiBaseUrl = baseUrl || backendConfig.baseUrl;
  
  // If endpoint is a full URL, use it directly; otherwise construct it
  let jobEndpoint = endpoint;
  if (!endpoint.startsWith('http')) {
    // If endpoint doesn't start with http, assume it's a relative path
    const base = apiBaseUrl.endsWith('/api') ? apiBaseUrl : `${apiBaseUrl}/api`;
    jobEndpoint = endpoint.startsWith('/') ? `${base}${endpoint}` : `${base}/${endpoint}`;
  }

  try {
    // Submit the job with timeout
    console.log('📝 Submitting job to:', jobEndpoint);
    
    // Create AbortController for timeout.
    // 10s is too aggressive for local/staging cold starts and causes false "Request timeout" failures.
    const controller = new AbortController();
    // Job enqueue should return quickly (202 + jobId). Style-transfer uses that path on local backend too.
    const submitTimeoutMs =
      typeof endpoint === 'string' && endpoint.includes('art-history-appreciation')
        ? 90000
        : 30000;
    const timeoutId = setTimeout(() => controller.abort(), submitTimeoutMs);
    
    let submitResponse;
    let useFallback = false;
    try {
      submitResponse = await fetch(jobEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...aiQueue.getAuthHeaders()
        },
        body: JSON.stringify(data),
        signal: controller.signal
      });
      clearTimeout(timeoutId);
    } catch (fetchError) {
      clearTimeout(timeoutId);
      if (fetchError.name === 'AbortError' || fetchError.message.includes('timeout')) {
        console.warn('⏱️ Request timeout, trying fallback endpoint...');
        useFallback = true;
      } else if (fetchError.message.includes('CORS') || fetchError.message.includes('Failed to fetch') || fetchError.message.includes('Load failed')) {
        console.warn('❌ CORS or network error, trying fallback endpoint...');
        useFallback = true;
      } else {
        throw fetchError;
      }
    }

    // If we need to use fallback, try the regular API endpoint
    if (useFallback || (submitResponse && !submitResponse.ok && submitResponse.status >= 500)) {
      const fallbackEndpoint = endpoint.includes('generateAppreciation') 
        ? `${apiBaseUrl}/api/artwork-appreciation`
        : endpoint.includes('art-history-appreciation')
        ? `${apiBaseUrl}/api/art-history-appreciation`
        : endpoint;
      
      if (fallbackEndpoint !== jobEndpoint) {
        console.log('🔄 Retrying with fallback endpoint:', fallbackEndpoint);
        try {
          submitResponse = await fetch(fallbackEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...aiQueue.getAuthHeaders()
            },
            body: JSON.stringify(data)
          });
        } catch (fallbackError) {
          throw new Error(`Both primary and fallback endpoints failed: ${fallbackError.message}`);
        }
      } else if (useFallback) {
        throw new Error('Request timeout - the server took too long to respond');
      }
    }

    if (!submitResponse.ok) {
      const errorText = await submitResponse.text().catch(() => '');
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText || `Job submission failed with status ${submitResponse.status}` };
      }
      throw new Error(errorData.error || errorData.message || `Job submission failed with status ${submitResponse.status}`);
    }

    // Safely parse JSON response - handle empty or incomplete responses
    let submitResult;
    try {
      const responseText = await submitResponse.text();
      if (!responseText || responseText.trim() === '') {
        throw new Error('Empty response from server');
      }
      submitResult = JSON.parse(responseText);
    } catch (parseError) {
      console.error('❌ Failed to parse job submission response:', parseError);
      throw new Error(`Failed to parse server response: ${parseError.message}. Please try again.`);
    }
    const jobId = submitResult.jobId || submitResult.id || submitResult.job_id;

    if (!jobId) {
      // If no jobId returned, assume synchronous response
      console.log('✅ Synchronous response received (no jobId)');
      return submitResult;
    }

    console.log(`📝 Job ${jobId} submitted, starting polling...`);

    // Poll for job completion
    return new Promise((resolve, reject) => {
      let attempts = 0;
      let pollInterval;

      const poll = async () => {
        try {
          attempts++;
          // Always log so user sees polling progress (BFL can take 30–90s)
          console.log(`📝 Poll #${attempts} job ${jobId} – checking status...`);

          // Get job status - unified endpoint is /api/job-status/:jobId
          const statusBase = apiBaseUrl.endsWith('/api') ? apiBaseUrl : `${apiBaseUrl}/api`;
          const statusUrl = `${statusBase}/job-status/${jobId}`;

          const statusResponse = await fetch(statusUrl, {
            method: 'GET',
            headers: { ...aiQueue.getAuthHeaders() },
            credentials: 'omit'
          });

          if (!statusResponse.ok) {
            console.log(`📝 Poll #${attempts} job ${jobId}: HTTP ${statusResponse.status}`);
            // 404 in first few attempts: job doc may not be visible yet, retry
            if (statusResponse.status === 404 && attempts <= 5) {
              console.log(`📝 Job ${jobId} not found yet (attempt ${attempts}), retrying in 1s...`);
              pollInterval = setTimeout(poll, Math.min(interval, 1000));
              return;
            }
            if (attempts >= maxAttempts) {
              clearInterval(pollInterval);
              reject(new Error(`Job status check failed after ${attempts} attempts`));
              return;
            }
            pollInterval = setTimeout(poll, interval);
            return;
          }

          let status;
          try {
            const statusText = await statusResponse.text();
            if (!statusText || statusText.trim() === '') {
              throw new Error('Empty status response');
            }
            status = JSON.parse(statusText);
          } catch (parseErr) {
            clearInterval(pollInterval);
            console.error(`❌ Failed to parse job status response for job ${jobId}:`, parseErr);
            reject(new Error(`Invalid job status response: ${parseErr.message}`));
            return;
          }

          const currentStatus = status?.status ?? status?.state ?? 'unknown';
          // Always log poll progress so user sees activity (BFL can take 30–90s)
          console.log(`📝 Poll #${attempts} job ${jobId}: ${currentStatus}${status?.result ? ' (has result)' : ''}`);

          // Call status change callback
          if (onStatusChange) {
            onStatusChange(status);
          }

          // Call progress callback
          if (onProgress) {
            onProgress(status, { jobId }, attempts);
          }

          // Check if job is complete (backend returns status: 'completed'; also accept art-history / compare shapes)
          const jr = status?.result;
          const looksLikeArtHistoryJob =
            jr &&
            typeof jr === 'object' &&
            (Array.isArray(jr.appreciation) ||
              Array.isArray(jr.hotspots) ||
              jr.agenticFormat != null ||
              jr.agenticAnalysis != null);
          const completed =
            status?.state === 'completed' ||
            status?.status === 'completed' ||
            (jr != null &&
              (jr.title != null ||
                Array.isArray(jr.steps) ||
                looksLikeArtHistoryJob));
          if (completed) {
            clearInterval(pollInterval);
            console.log(`✅ Job ${jobId} completed successfully`);
            const raw = status.result != null ? status.result : (status.data != null ? status.data : status);
            // Only normalize to activity shape when result looks like flow-activity (title/steps); else return as-is (e.g. compare-artworks: similarities, differences, insight)
            const isActivityResult = raw && typeof raw === 'object' && (raw.title != null || Array.isArray(raw.steps));
            const result = isActivityResult && typeof raw === 'object'
              ? {
                  title: raw.title ?? '',
                  objective: raw.objective ?? raw.whatYoullMake ?? '',
                  materials: Array.isArray(raw.materials) ? raw.materials : [],
                  steps: Array.isArray(raw.steps) ? raw.steps : (Array.isArray(raw.stepByStep) ? raw.stepByStep : []),
                  beforeYouStart: Array.isArray(raw.beforeYouStart) ? raw.beforeYouStart : undefined,
                  techniqueMix: Array.isArray(raw.techniqueMix) ? raw.techniqueMix : undefined,
                  keyTips: Array.isArray(raw.keyTips) ? raw.keyTips : undefined,
                  bonusChallenge: raw.bonusChallenge,
                  tinyReflection: Array.isArray(raw.tinyReflection) ? raw.tinyReflection : undefined,
                }
              : raw;
            resolve(result);
            return;
          }

          // Check if job failed
          if (status?.state === 'failed' || status?.status === 'failed') {
            clearInterval(pollInterval);
            const errorMsg = status?.error || status?.message || 'Job failed';
            console.error(`❌ Job ${jobId} failed:`, errorMsg);
            reject(new Error(errorMsg));
            return;
          }

          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            console.error(`⏱️ Job ${jobId} polling timeout after ${attempts} attempts`);
            reject(new Error(`Job polling timeout after ${attempts} attempts`));
            return;
          }

          pollInterval = setTimeout(poll, interval);
        } catch (error) {
          clearInterval(pollInterval);
          console.error(`❌ Error polling job ${jobId} (attempt ${attempts}):`, error?.message || error);
          reject(error);
        }
      };

      // Start polling
      poll();
    });
  } catch (error) {
    console.error('❌ Error in enqueueAndPoll:', error);
    throw error;
  }
}
